import React, { FC, useMemo } from 'react';

import { FieldArray, useFormikContext } from 'formik';

import SearchableSelect from '../../../../../../components/inputs/searchableSelect';
import TextInput from '../../../../../../components/inputs/textInput';
import styled from 'styled-components';

import aampeTheme from '../../../../../../constants/aampeTheme';
import {
    iosButtonFields,
    NotificationAndroidActionType,
    NotificationAndroidOptions,
} from './constants';

import {
    NotificationButtonElement,
    NotificationButtonFormValues,
} from '../../../../../../models/formula.model';
import { IosCategoryIdentifier } from '../../../../../../models/notificationButton.model';

const IosButtonFields: FC = () => {
    const { setFieldValue, values, touched, errors, handleBlur } =
        useFormikContext<NotificationButtonFormValues>();

    const { iosNotificationCategory, iosButtons } = values;
    const fields = useMemo<NotificationButtonElement[]>(
        () =>
            iosButtonFields?.[
                iosNotificationCategory as IosCategoryIdentifier
            ] || [],
        [iosNotificationCategory],
    );

    return (
        <FieldArray
            name="iosButtons"
            render={() => (
                <Content>
                    {fields.map((field, index) => {
                        return (
                            <FieldContent key={index}>
                                <FieldContainer>
                                    <Title>
                                        iOS category action identifier
                                    </Title>
                                    <StyledTextField
                                        onChange={() => {}}
                                        value={field?.text}
                                        isDisabled={!!field?.text!}
                                    />
                                </FieldContainer>
                                <SelectFieldContainer>
                                    <Title>On click action</Title>
                                    <SearchableSelect
                                        name={field?.action!}
                                        options={NotificationAndroidOptions.filter(
                                            (item) =>
                                                item.value !==
                                                NotificationAndroidActionType.CLOSE,
                                        )}
                                        onChange={(field, value) => {
                                            setFieldValue(
                                                `iosButtons[${index}].action`,
                                                (value as any).value,
                                            );
                                        }}
                                        value={NotificationAndroidOptions.find(
                                            (item) =>
                                                item.value ===
                                                iosButtons[index]?.action,
                                        )}
                                        placeholder={'Select on click action'}
                                        isDisabled={
                                            NotificationAndroidOptions.find(
                                                (item) =>
                                                    item.value ===
                                                    field?.action!,
                                            )?.value ===
                                            NotificationAndroidActionType.CLOSE
                                        }
                                    />
                                </SelectFieldContainer>
                                {[
                                    NotificationAndroidActionType.DEEP_LINK,
                                    NotificationAndroidActionType.URI,
                                ].includes(
                                    iosButtons[index]
                                        ?.action as NotificationAndroidActionType,
                                ) && (
                                    <FieldContainer>
                                        <Title>URL</Title>
                                        <StyledTextField
                                            placeholder="Enter url"
                                            value={iosButtons[index]?.uri!}
                                            onChange={(e) =>
                                                setFieldValue(
                                                    `iosButtons[${index}].uri`,
                                                    e.target.value,
                                                )
                                            }
                                            name={'uri'}
                                            onBlur={handleBlur}
                                        />
                                        <ErrorMessage>
                                            {!!iosButtons?.[index]?.uri &&
                                                (touched as any)?.uri &&
                                                (
                                                    errors?.iosButtons?.[
                                                        index
                                                    ] as any
                                                )?.uri}
                                        </ErrorMessage>
                                    </FieldContainer>
                                )}
                            </FieldContent>
                        );
                    })}
                </Content>
            )}
        />
    );
};

const Title = styled.div<{ isBold?: boolean }>(({ isBold }) => ({
    fontSize: aampeTheme.size.font.default,
    fontWeight: isBold ? aampeTheme.weights.medium : aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.small,
    color: aampeTheme.colors.neutral09,
}));

const SelectFieldContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
});

const FieldContent = styled.div({
    backgroundColor: aampeTheme.colors.backgroundWG02,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(4),
    gap: aampeTheme.spacing4(4),
    display: 'flex',
    flexDirection: 'column',
});

const FieldContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
});

const StyledTextField = styled(TextInput)({
    width: '100%',
    '& .MuiInputBase-root': {
        backgroundColor: aampeTheme.colors.white,
        borderRadius: aampeTheme.borderRadius.default,
        borderColor: aampeTheme.colors.bordersWG01,
        minHeight: '38px',
        '&:hover fieldset': {
            borderColor: aampeTheme.colors.solidA01,
        },
    },
});

const ErrorMessage = styled.span({
    color: aampeTheme.colors.error,
    fontSize: aampeTheme.size.font.textSmall,
});

const Content = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    maxHeight: '300px',
    overflowY: 'auto',
});

export default IosButtonFields;
